import React from 'react';
import PropTypes from 'prop-types'
import * as types from '../../../constants/actionTypes';
import { FILE_STATUSES } from '../../../constants/constants';

import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Icon from '../../Common/Icon';
import TextArea from '../../Common/TextArea';
import InputText from '../../Common/InputText';
import Button from '../../Common/Button';
import FileChooser from '../../Common/FileChooser';
import FileTypeIcon from '../../Common/FileTypeIcon';

const FilesAndLinksSection = (props) => {

    const anyLinksBeingEdited = () => {
        return props.task.links && props.task.links.filter(x => x.isEditing || x.isAdding).length > 0;
    }

    const addLink = () => {
        let links = props.task.links.slice();
        links.push({
            id: 0,
            locationTaskId: props.task.id,
            baselineTaskLinkId: null,
            ordinal: links.length + 1,
            name: "",
            editing_name: "",
            url: "",
            editing_url: "",
            isActive: true,
            isAdding: true,
            isEditing: false
        });
        props.setTaskLinks(links);
    }

    const onCancelLink = (link) => {
        const links = props.task.links.slice();
        const index = links.findIndex(x => x.ordinal === link.ordinal);
        if (link.isAdding) {
            // Remove the last link from the list, which is the one that was being added
            links.pop();
        } else if (link.isEditing) {
            // Clear the editing values
            link.editing_url = "";
            link.editing_name = "";
            link.isEditing = false;
            links.splice(index, 1, link);
        }
        props.setTaskLinks(links);
    }

    const updateLink = (link, fieldName, fieldValue) => {
        let links = props.task.links.slice();
        const index = links.findIndex(x => x.ordinal === link.ordinal);
        link[fieldName] = fieldValue;
        links.splice(index, 1, link);
        props.setTaskLinks(links);
    }

    const onEditLink = (link) => {
        // Don't allow multiple links to be edited at once
        if (anyLinksBeingEdited()) {
            return;
        }

        const links = props.task.links.slice();
        const index = links.findIndex(x => x.ordinal === link.ordinal);
        link.isEditing = true;
        link.isAdding = false;
        link.editing_url = link.url;
        link.editing_name = link.name;
        links.splice(index, 1, link);
        props.setTaskLinks(links);
    }

    const onDeleteLink = async (link) => {
        try {
            link.isActive = false;
            props.setSavingLink(true);
            const response = await props.locationTaskActions.updateLink(link);
            if (response.type === types.UPDATE_LOCATION_TASK_LINK_SUCCESS) {
                Notification("success", "Link Deleted Successfully", "");
                const links = props.task.links.slice();
                const index = links.findIndex(x => x.ordinal === link.ordinal);
                links.splice(index, 1);
                props.setTaskLinks(links);
            } else if (response.type === types.UPDATE_LOCATION_TASK_LINK_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
            props.setSavingLink(false);
        } catch (error) {
            Notification("error", "Error", error);
            props.setSavingLink(false);
        }
    }

    const anyFilesBeingEdited = () => {
        return props.task.files && props.task.files.filter(x => x.isEditing || x.isAdding).length > 0;
    }

    const updateFile = (file, fieldName, fieldValue) => {
        let files = props.task.files.slice();
        const index = files.findIndex(x => x.id === file.id);
        file[fieldName] = fieldValue;
        files.splice(index, 1, file);
        props.setTaskFiles(files);
    }

    const onCancelFile = (file) => {
        const files = props.task.files.slice();
        const index = files.findIndex(x => x.id === file.id);
        if (file.isAdding) {
            // Remove the last file from the list, which is the one that was being added
            files.pop();
        } else if (file.isEditing) {
            // Clear the editing values
            file.editing_name = file.name;
            file.editing_description = file.description;
            file.editing_fileStatusId = file.fileStatusId;
            file.isEditing = false;
            file.showFileInput = false;
            files.splice(index, 1, file);
        }
        props.setTaskFiles(files);
    }

    const onEditFile = (file) => {
        // Don't allow multiple files to be edited at once
        if (anyFilesBeingEdited()) {
            return;
        }

        const files = props.task.files.slice();
        const index = files.findIndex(x => x.id === file.id);
        file.editing_name = file.name ? file.name : "";
        file.editing_description = file.description ? file.description : "";
        file.editing_fileStatusId = file.fileStatusId ? file.fileStatusId : FILE_STATUSES.Pending;
        file.isEditing = true;
        file.isAdding = false;
        files.splice(index, 1, file);
        props.setTaskFiles(files);
    }

    const onDeleteFile = async (file) => {
        try {
            props.setSavingFile(true);
            const request = {
                ...file,
                isActive: false, // Deactivate the file
                locationTaskId: props.task.id,
            };
            const response = await props.locationTaskActions.deleteFile(request);
            if (response.type === types.DELETE_LOCATION_TASK_FILE_SUCCESS) {
                const files = props.task.files.slice();
                const index = files.findIndex(x => x.id === file.id);
                files.splice(index, 1);
                props.setTaskFiles(files);
                props.setSavingFile(false);
                Notification("success", "File Deleted Successfully", "");
            } else if (response.type === types.DELETE_LOCATION_TASK_FILE_FAILURE) {
                Notification("error", "Error", response.payload.message);
                props.setSavingFile(false);
            }
        } catch (error) {
            Notification("error", "Error", error);
            props.setSavingFile(false);
        }
    }

    const onDownloadFile = async (file, e) => {
        e.preventDefault();
        try {
            props.setSavingFile(true);
            await props.filesActions.download(file.fileName);
            props.setSavingFile(false);
        } catch (error) {
            Notification("error", "Error", error);
            props.setSavingFile(false);
        }
    }

    // this is for private files that were converted to links (link.baselineTaskLinkId != null)
    const onDownloadLink = async (link, e) => {
        e.preventDefault();
        try {
            props.setSavingFile(true);

            // extract the file name from the link.url
            let start = link.url.indexOf("=") + 1;
            let fileName = link.url.substr(start, link.url.length - start);

            await props.filesActions.download(fileName);
            props.setSavingFile(false);
        } catch (error) {
            Notification("error", "Error", error);
            props.setSavingFile(false);
        }
    }

    const onFileReplaced = (file, e) => {
        const replacedFile = e.target.files[0];
        if (replacedFile) {
            updateFile(file, "file", replacedFile);
        }
    }

    const links = props.task.links;
    const files = props.task.files;

    const showAddLink = !(links && links.filter(x => x.isEditing || x.isAdding).length);
    const showUploadFile = !(files && files.filter(x => x.isEditing || x.isAdding).length);
    const linksExist = links && links.length > 0;
    const filesExist = files && files.length > 0;

    /**
     * If both the links and files lists are empty, then show the add a link and upload a file
     * button links next to each other, instead of in their typical block display at the end
     * of their respective lists
     */
    const emptyListsButtonOverrideStyle = { 
        display: "inline-block", 
        padding: "6px 12px 6px 0px", 
        verticalAlign: "top",
        margin: "0px"
    };
    const buttonStyle = (!filesExist) ? emptyListsButtonOverrideStyle : {};

    return (
        <div className="files-and-links-section">
            {
                linksExist &&
                links.map(link => {
                    if (link.baselineTaskLinkId && link.customerFileId) {
                        return (
                            <div key={link.ordinal} className="link row no-gutters">
                                <span className="col-1"><Icon name="link" /></span>
                                <Button type="button" bootstrapClass="link" onClick={(e) => onDownloadLink(link, e)} text={link.name} />
                            </div>
                        );
                    } else {
                        if (!link.isEditing && !link.isAdding) {
                            return (
                                <div key={link.ordinal} className="link row no-gutters">
                                    <span className="col-1"><Icon name="link" /></span>
                                    <a className="col-10" href={link.url} target="_blank" rel="noopener noreferrer">{link.name}</a>
                                    {!link.baselineTaskLinkId &&
                                        <span className="col-1 actionable-icon" title="Edit" onClick={() => onEditLink(link)}><Icon name="pencil-alt" /></span>
                                    }
                                </div>
                            );
                        } else {
                            return (
                                <div className="link-form" key={link.ordinal}>
                                    <LoadingIndicator isLoading={props.isSavingLink}>
                                        <table className="links-table">
                                            <tbody>
                                                <tr>
                                                    <td><InputText className="col-6" isFocused={true} name="linkurl" label="Link URL" type="url" placeholder="Link URL"
                                                        value={link.editing_url}
                                                        onChange={(e) => updateLink(link, "editing_url", e.target.value)}
                                                        isRequired={true}
                                                        pattern="https?://.*"
                                                        errorMessage="Link URL must include http(s)://"
                                                        maxLength={500}
                                                    />
                                                    </td>
                                                    <td className="td-spacer"></td>
                                                    <td><InputText className="col-6" name="linkname" label="Link Abbreviation" type="text" placeholder="Link Abbreviation"
                                                        value={link.editing_name}
                                                        onChange={(e) => updateLink(link, "editing_name", e.target.value)}
                                                        isRequired={true}
                                                        errorMessage="Link Abbreviation Required"
                                                        maxLength={50}
                                                    />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <div className="button-wrapper">
                                            <Button type="button" bootstrapClass="secondary" text="Save Link" onClick={() => props.onSaveLink(link)} />
                                            <Button type="button" bootstrapClass="light" text="Cancel" onClick={() => onCancelLink(link)} />
                                            {
                                                link.isEditing &&
                                                <Button type="button" bootstrapClass="danger" text="Delete" onClick={() => onDeleteLink(link)} />
                                            }
                                        </div>
                                    </LoadingIndicator>
                                </div>
                            );
                        }
                    }
                })
            }
            {
                showAddLink && props.isMobile === false &&
                <button className="add-a-link btn btn-link" onClick={addLink} style={buttonStyle}>Add a Link</button>
            }
            {
                filesExist &&
                files.map(file => {
                    const fileIsApprovedOrDenied = file.fileStatusId === FILE_STATUSES.Approved || file.fileStatusId === FILE_STATUSES.Denied;
                    const fileNameClass = fileIsApprovedOrDenied ? "col-9" : "col-10";

                    if (!file.isAdding && !file.isEditing) {
                        return (
                            <div key={file.fileName} className="file row no-gutters">
                                <span className="col-1 text-left"><FileTypeIcon extension={file.extension} /></span>
                                <button className={"btn btn-link " + fileNameClass} title="Download" onClick={(e) => onDownloadFile(file, e)}>{file.name}</button>
                                {
                                    file.fileStatusId === FILE_STATUSES.Approved
                                        ?
                                        <span className="col-1 approved" title="File Approved"><Icon name="check-circle" /></span>
                                        :
                                        file.fileStatusId === FILE_STATUSES.Denied
                                            ?
                                            <span className="col-1 denied" title="File Denied"><Icon name="times-circle" /></span>
                                            :
                                            null
                                }
                                {
                                    props.isMobile === false &&
                                        <span className="col-1 actionable-icon" title="Edit" onClick={() => onEditFile(file)}><Icon name="pencil-alt" /></span>
                                }
                            </div>
                        );
                    } else {
                        const unselectedClass = "btn btn-muted";
                        const approvedClass = file.editing_fileStatusId === FILE_STATUSES.Approved ? "btn btn-success" : unselectedClass;
                        const deniedClass = file.editing_fileStatusId === FILE_STATUSES.Denied ? "btn btn-danger" : unselectedClass;
                        const pendingClass = file.editing_fileStatusId === FILE_STATUSES.Pending ? "btn btn-secondary" : unselectedClass;

                        const showReplaceAndDeleteLinks = file.isEditing && !file.showFileInput;

                        return (
                            <div key={file.fileName} className="upload-form">
                                <LoadingIndicator isLoading={props.isSavingFile}>
                                    <InputText name="fileName" label="File Name" type="text" placeholder="File Name"
                                        isFocused={true}
                                        value={file.editing_name}
                                        onChange={(e) => updateFile(file, "editing_name", e.target.value)}
                                        isRequired={true}
                                        errorMessage="File Name is required"
                                        maxLength={100} />
                                    <TextArea rows={5} maxLength={500} name="fileDescription" label="File Description" type="text" placeholder="File Description"
                                        value={file.editing_description}
                                        onChange={(e) => updateFile(file, "editing_description", e.target.value)} />
                                    <div className="remaining-chars text-end">{file.editing_description.length}/500</div>
                                    {
                                        showReplaceAndDeleteLinks && props.isMobile === false &&
                                        <div className="replace-and-delete-wrapper">
                                            <button type="button" className="btn btn-link replace-file-link" onClick={() => updateFile(file, "showFileInput", true)}>Replace File</button>
                                            <button type="button" className="btn btn-link delete-file-link" onClick={() => onDeleteFile(file)}>Delete File</button>
                                        </div>
                                    }
                                    {
                                        file.showFileInput && props.isMobile === false &&
                                        <div className="file-chooser-wrapper">
                                            <FileChooser name="file"
                                                onChange={(e) => onFileReplaced(file, e)}
                                                isRequired={true}
                                                errorMessage="A file is required"
                                            />
                                        </div>
                                    }
                                    {
                                        file.isEditing &&
                                        <div className="approval-status-wrapper text-center">
                                            <div className="btn-group" role="group" aria-label="Approval Status">
                                                <button type="button" className={pendingClass} onClick={() => updateFile(file, "editing_fileStatusId", FILE_STATUSES.Pending)}>Pending</button>
                                                <button type="button" className={approvedClass} onClick={() => updateFile(file, "editing_fileStatusId", FILE_STATUSES.Approved)}>Approved</button>
                                                <button type="button" className={deniedClass} onClick={() => updateFile(file, "editing_fileStatusId", FILE_STATUSES.Denied)}>Denied</button>
                                            </div>
                                        </div>
                                    }
                                    <div className="button-wrapper">
                                        <Button type="button" bootstrapClass="secondary" text="Save File" onClick={() => props.onSaveFile(file)} />
                                        <Button type="button" bootstrapClass="light" text="Cancel" onClick={() => onCancelFile(file)} />
                                    </div>
                                </LoadingIndicator>
                            </div>
                        );
                    }
                })
            }
            {
                showUploadFile && props.isMobile === false &&
                <button className="me-3 btn btn-link" onClick={(e) => props.uploadFile(e)} style={buttonStyle}>Upload a File</button>
            }
        </div>
    );
}

FilesAndLinksSection.propTypes = {
    task: PropTypes.object.isRequired,
    isSavingLink: PropTypes.bool.isRequired,
    isSavingFile: PropTypes.bool.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    filesActions: PropTypes.object.isRequired,
    setTaskLinks: PropTypes.func.isRequired,
    onSaveLink: PropTypes.func.isRequired,
    setSavingLink: PropTypes.func.isRequired,
    uploadFile: PropTypes.func.isRequired,
    setTaskFiles: PropTypes.func.isRequired,
    onSaveFile: PropTypes.func.isRequired,
    setSavingFile: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired
};

export default FilesAndLinksSection;
